import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { analyseDocument, computeDocumentFlags, isAiConfigured } from '../../../services/aiDocumentService';
import { documentService } from '../../../services/documentService';

const CATEGORY_OPTIONS = [
  { value: 'rams', label: 'H&S Docs' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'training', label: 'Training Certificates' },
  { value: 'plant', label: 'Plant Certificates' },
  { value: 'inspection_report', label: 'Inspection Report' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'equipment_certificate', label: 'Equipment Certificate' }
];

function ConfidenceBadge({ value }) {
  if (value == null) return null;
  const pct = Math.round((value || 0) * 100);
  const color = pct >= 80 ? 'text-success' : pct >= 50 ? 'text-warning' : 'text-muted-foreground';
  return (
    <span className={`text-xs font-medium ml-1 ${color}`}>{pct}% confidence</span>
  );
}

function FlagsPanel({ flags }) {
  if (!flags?.length) return null;
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-foreground">Document Flags</p>
      {flags?.map((flag, i) => (
        <div
          key={i}
          className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
            flag?.severity === 'error' ?'bg-error/10 border border-error/20 text-error' :'bg-warning/10 border border-warning/20 text-warning'
          }`}
        >
          <Icon
            name={flag?.severity === 'error' ? 'AlertCircle' : 'AlertTriangle'}
            size={14}
            className="flex-shrink-0"
          />
          {flag?.label}
        </div>
      ))}
    </div>
  );
}

const UploadDocumentModal = ({ isOpen, onClose, onUpload, siteId, companyId }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    owner: '',
    issueDate: '',
    expiryDate: '',
    notes: '',
    certificateNumber: '',
    policyNumber: '',
    contractorName: '',
    inspectionDate: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  // AI state
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [aiDismissed, setAiDismissed] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [confidence, setConfidence] = useState({});
  const [flags, setFlags] = useState([]);
  const [existingDocs, setExistingDocs] = useState([]);

  // Load existing docs for duplicate check
  useEffect(() => {
    if (!siteId) return;
    documentService?.getDocumentsBySite(siteId)?.then(({ data }) => {
      setExistingDocs(data || []);
    });
  }, [siteId]);

  // Recompute flags whenever relevant fields change
  useEffect(() => {
    setFlags(computeDocumentFlags(formData, existingDocs));
  }, [formData?.expiryDate, formData?.title, formData?.certificateNumber, existingDocs]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleFileChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const validTypes = [
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg', 'image/png', 'image/jpg'
    ];
    if (!validTypes?.includes(file?.type)) {
      setErrors(prev => ({ ...prev, file: 'Invalid file type. Only PDF, DOC, DOCX, JPG, and PNG files are allowed' }));
      return;
    }
    if (file?.size > 10 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, file: 'File size must be less than 10MB' }));
      return;
    }
    setSelectedFile(file);
    setErrors(prev => ({ ...prev, file: '' }));

    const nameWithoutExt = file?.name?.replace(/\.[^/.]+$/, '');
    if (!formData?.title) handleChange('title', nameWithoutExt);

    // AI autofill is images-only for now — PDFs would need the Responses
    // API via a new edge function path. Keep this check in sync with the
    // document-management UploadDocumentModal.
    const isPdf = file?.type === 'application/pdf';
    const canAnalyse = file?.type?.startsWith('image/');
    if (canAnalyse && isAiConfigured()) {
      setIsAnalysing(true);
      setAiError(null);
      setConfidence({});
      const { suggestions, confidence: conf, error } = await analyseDocument(file);
      setIsAnalysing(false);

      if (error) {
        setAiError(error);
      } else if (suggestions) {
        setConfidence(conf || {});
        setFormData(prev => ({
          ...prev,
          title: suggestions?.title || prev?.title || nameWithoutExt,
          category: suggestions?.category || prev?.category,
          issueDate: suggestions?.issue_date || prev?.issueDate,
          expiryDate: suggestions?.expiry_date || prev?.expiryDate,
          certificateNumber: suggestions?.certificate_number || prev?.certificateNumber,
          policyNumber: suggestions?.policy_number || prev?.policyNumber,
          contractorName: suggestions?.contractor_name || prev?.contractorName,
          inspectionDate: suggestions?.inspection_date || prev?.inspectionDate,
          notes: suggestions?.site_name
            ? `Site: ${suggestions?.site_name}${prev?.notes ? '\n' + prev?.notes : ''}`
            : prev?.notes
        }));
      }
    } else if (isPdf) {
      setAiError('AI autofill supports image files. PDFs can be filled in manually.');
    } else if (!isAiConfigured()) {
      setAiError('not_configured');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.title?.trim()) newErrors.title = 'Document title is required';
    if (!formData?.category) newErrors.category = 'Category is required';
    if (!formData?.owner?.trim()) newErrors.owner = 'Owner is required';
    if (!formData?.issueDate) newErrors.issueDate = 'Issue date is required';
    if (!formData?.expiryDate) newErrors.expiryDate = 'Expiry date is required';
    if (formData?.issueDate && formData?.expiryDate) {
      if (new Date(formData.expiryDate) <= new Date(formData.issueDate)) {
        newErrors.expiryDate = 'Expiry date must be after issue date';
      }
    }
    if (!selectedFile) newErrors.file = 'Please select a file to upload';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsUploading(true);
    try {
      await onUpload({ ...formData, file: selectedFile, siteId, companyId });
      handleClose();
    } catch (error) {
      setErrors(prev => ({ ...prev, submit: error?.message || 'Failed to upload document' }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFormData({ title: '', category: '', owner: '', issueDate: '', expiryDate: '', notes: '', certificateNumber: '', policyNumber: '', contractorName: '', inspectionDate: '' });
    setSelectedFile(null);
    setErrors({});
    setIsUploading(false);
    setIsAnalysing(false);
    setAiError(null);
    setConfidence({});
    setFlags([]);
    setAiDismissed(false);
    onClose();
  };

  if (!isOpen) return null;

  const showAiNotice = !aiDismissed && aiError === 'not_configured';
  const showAiFailed = !aiDismissed && aiError && aiError !== 'not_configured';
  const hasConfidence = Object.keys(confidence)?.length > 0;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
              <Icon name="Upload" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Upload Document</h2>
              <p className="text-sm text-muted-foreground">Upload a new document with details</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose} aria-label="Close modal" disabled={isUploading}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-4">

            {/* AI unavailable notice */}
            {showAiNotice && (
              <div className="flex items-start gap-3 p-3 bg-muted/50 border border-border rounded-md">
                <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground flex-1">AI autofill unavailable — fill in details manually</p>
                <button type="button" onClick={() => setAiDismissed(true)} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={14} />
                </button>
              </div>
            )}

            {showAiFailed && (
              <div className="flex items-start gap-3 p-3 bg-muted/50 border border-border rounded-md">
                <Icon name="AlertCircle" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground flex-1">{aiError}</p>
                <button type="button" onClick={() => setAiDismissed(true)} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={14} />
                </button>
              </div>
            )}

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                File <span className="text-error">*</span>
              </label>
              <div className="relative">
                <input
                  id="document-file-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => document?.getElementById('document-file-input')?.click()}
                  className="w-full px-4 py-3 bg-background border-2 border-dashed border-input rounded-md text-left hover:border-accent transition-smooth flex items-center gap-3"
                >
                  <Icon name="Upload" size={20} color="var(--color-accent)" />
                  <div className="flex-1">
                    {selectedFile ? (
                      <div>
                        <p className="text-sm font-medium text-foreground">{selectedFile?.name}</p>
                        <p className="text-xs text-muted-foreground">{(selectedFile?.size / 1024 / 1024)?.toFixed(2)} MB</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-foreground">Click to select file</p>
                        <p className="text-xs text-muted-foreground">PDF, DOC, DOCX, JPG, PNG (max 10MB)</p>
                      </div>
                    )}
                  </div>
                </button>
              </div>
              {errors?.file && <p className="text-sm text-error mt-1">{errors?.file}</p>}
            </div>

            {/* AI analysing state */}
            {isAnalysing && (
              <div className="flex items-center gap-3 p-3 bg-accent/5 border border-accent/20 rounded-md">
                <Icon name="Loader2" size={16} className="text-accent animate-spin flex-shrink-0" />
                <p className="text-sm text-accent">Analysing document...</p>
              </div>
            )}

            {/* AI autofill banner */}
            {hasConfidence && !isAnalysing && (
              <div className="flex items-center gap-2 p-3 bg-accent/5 border border-accent/20 rounded-md">
                <Icon name="Sparkles" size={16} className="text-accent flex-shrink-0" />
                <p className="text-sm text-accent">Fields pre-filled by AI — review and edit as needed</p>
              </div>
            )}

            {/* Document Title */}
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-sm font-medium text-foreground">Document Title <span className="text-error">*</span></label>
                {hasConfidence && <ConfidenceBadge value={confidence?.title} />}
              </div>
              <Input
                type="text"
                placeholder="Enter document title"
                value={formData?.title}
                onChange={(e) => handleChange('title', e?.target?.value)}
                error={errors?.title}
                required
              />
            </div>

            {/* Category and Owner */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="text-sm font-medium text-foreground">Category <span className="text-error">*</span></label>
                  {hasConfidence && <ConfidenceBadge value={confidence?.category} />}
                </div>
                <Select
                  options={CATEGORY_OPTIONS}
                  value={formData?.category}
                  onChange={(value) => handleChange('category', value)}
                  error={errors?.category}
                  required
                />
              </div>
              <Input
                label="Owner"
                type="text"
                placeholder="Enter owner name"
                value={formData?.owner}
                onChange={(e) => handleChange('owner', e?.target?.value)}
                error={errors?.owner}
                required
              />
            </div>

            {/* Contractor Name */}
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-sm font-medium text-foreground">Contractor / Company Name</label>
                {hasConfidence && <ConfidenceBadge value={confidence?.contractor_name} />}
              </div>
              <Input
                type="text"
                placeholder="Contractor or company name"
                value={formData?.contractorName}
                onChange={(e) => handleChange('contractorName', e?.target?.value)}
              />
            </div>

            {/* Certificate + Policy numbers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="text-sm font-medium text-foreground">Certificate Number</label>
                  {hasConfidence && <ConfidenceBadge value={confidence?.certificate_number} />}
                </div>
                <Input
                  type="text"
                  placeholder="e.g. CERT-2024-001"
                  value={formData?.certificateNumber}
                  onChange={(e) => handleChange('certificateNumber', e?.target?.value)}
                />
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="text-sm font-medium text-foreground">Policy Number</label>
                  {hasConfidence && <ConfidenceBadge value={confidence?.policy_number} />}
                </div>
                <Input
                  type="text"
                  placeholder="e.g. POL-2024-001"
                  value={formData?.policyNumber}
                  onChange={(e) => handleChange('policyNumber', e?.target?.value)}
                />
              </div>
            </div>

            {/* Issue Date and Expiry Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="text-sm font-medium text-foreground">Issue Date <span className="text-error">*</span></label>
                  {hasConfidence && <ConfidenceBadge value={confidence?.issue_date} />}
                </div>
                <Input
                  type="date"
                  value={formData?.issueDate}
                  onChange={(e) => handleChange('issueDate', e?.target?.value)}
                  error={errors?.issueDate}
                  required
                />
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="text-sm font-medium text-foreground">Expiry Date <span className="text-error">*</span></label>
                  {hasConfidence && <ConfidenceBadge value={confidence?.expiry_date} />}
                </div>
                <Input
                  type="date"
                  value={formData?.expiryDate}
                  onChange={(e) => handleChange('expiryDate', e?.target?.value)}
                  error={errors?.expiryDate}
                  required
                />
              </div>
            </div>

            {/* Inspection Date */}
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-sm font-medium text-foreground">Inspection Date</label>
                {hasConfidence && <ConfidenceBadge value={confidence?.inspection_date} />}
              </div>
              <Input
                type="date"
                value={formData?.inspectionDate}
                onChange={(e) => handleChange('inspectionDate', e?.target?.value)}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
              <textarea
                value={formData?.notes}
                onChange={(e) => handleChange('notes', e?.target?.value)}
                placeholder="Add any additional notes about this document..."
                rows={3}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth resize-none"
              />
            </div>

            {/* Document Flags */}
            <FlagsPanel flags={flags} />

            {/* Submit Error */}
            {errors?.submit && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <p className="text-sm text-error flex-1">{errors?.submit}</p>
              </div>
            )}
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isUploading}>Cancel</Button>
          <Button type="submit" onClick={handleSubmit} iconName="Upload" iconPosition="left" disabled={isUploading || isAnalysing}>
            {isUploading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UploadDocumentModal;